import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import { TabInfo, Tabs } from '../../../components/Tabs';
import PlateGroupTable from './PlateGroupTable';
import RegisteredPlateTable from './RegisteredPlateTable';
import { useAuth } from '../../../context/Auth';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { RouteProp, useRoute } from '@react-navigation/native';
import { DashBoardParamList } from '../../../typings/Params';
import Dashboard from './Dashboard';
import PlateGroupDropdown from '../../../components/dashboard/PlateGroupDropdown';
import moment from 'moment';
import Badge from '../../../components/Badge';
import Filter from './Filter';
import ExportCsvButton from './ExportCsvButton';
import { MonitoredPlatesDashboardInfo } from '../../../services/central-api/dashboard-summary';
import { WindowInformation } from '../../../services/window-information';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { RootNavigation } from '../../../Routes';

export type PlateMonitoringDashboardSelectedTab = 'plateGroupsManagement' | 'registeredPlates' | 'dashboard' | '';

export default function DashboardPlateMonitoring({ navigation }: { navigation: RootNavigation; }) {
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();
    const route = useRoute<RouteProp<DashBoardParamList, 'DashboardPlateMonitoring'>>();

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<PlateMonitoringDashboardSelectedTab>(route.params?.selectedTab ?? '');
    const [tabs, setTabs] = useState<TabInfo[]>([]);

    const [dashboardData, setDashboardData] = useState<MonitoredPlatesDashboardInfo | undefined>(undefined);
    const [groupId, setGroupId] = useState<number>(route.params?.groupId ?? 0);
    const [dashboardCalculationTime, setDashboardCalculationTime] = useState<number | undefined>(0);
    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

    const [filters, setFilters] = useFilters<DashBoardParamList['DashboardPlateMonitoring']>({
        ...route.params,
        fixedPeriod: route.params?.fixedPeriod ?? 'weekly',
        beginIn: route.params?.beginIn ?? moment().subtract(7, 'days').valueOf(),
        endIn: route.params?.endIn ?? moment().valueOf()
    });

    useEffect(() => {
        const localTabs: TabInfo[] = [];

        if (authenticatedUser?.permissions.dashboard_module_access
            || authenticatedUser?.permissions.dashboard_see_monitoring_plates) {
            localTabs.push({
                key: 'dashboard',
                label: translate('dashboard'),
            });
        }

        if (authenticatedUser?.permissions.dashboard_module_access
            || authenticatedUser?.permissions.dashboard_manage_monitoring_plates_groups) {
            localTabs.push({
                key: 'plateGroupsManagement',
                label: translate('plateGroupsManagement'),
            });
        }

        if (authenticatedUser?.permissions.dashboard_module_access
            || authenticatedUser?.permissions.dashboard_manage_monitoring_plates_groups
            || authenticatedUser?.permissions.dashboard_manage_monitoring_plates) {
            localTabs.push({
                key: 'registeredPlates',
                label: translate('registeredPlates'),
            });
        }
        setTabs(localTabs);
    }, [authenticatedUser]);

    function getRegisterButtonText(): string {
        if (currentTab === 'plateGroupsManagement') {
            return translate('registerGroup');
        } else if (currentTab === 'registeredPlates') {
            return translate('registerPlate');
        } else if (currentTab === 'dashboard') {
            return translate('exportCSV');
        }
        return '';
    }

    useEffect(() => {
        if (!isFilterOpen) {
            navigation.setParams({
                ...filters,
                groupId: groupId !== 0 ? groupId : undefined
            });

            setFilters({
                ...filters,
                groupId: groupId !== 0 ? groupId : undefined
            });
        }
    }, [groupId]);

    useEffect(() => {
        if (route.params
            && route.params.selectedTab
            && route.params.selectedTab === 'registeredPlates') {
            navigation.setParams({
                ...route.params,
                selectedTab: undefined
            });
        }
    }, [route.params]);

    useEffect(() => {
        setDashboardCalculationTime(0);
    }, [currentTab, filters]);

    return <Container>
        <Header
            title={translate('plateMonitoring')}
            actions={<>
                {currentTab === 'dashboard'
                    ? <View style={{ flexDirection: 'row', gap: 8 }}>
                        {!windowInfo.isMobile && <PlateGroupDropdown
                            value={groupId}
                            setValue={setGroupId}
                            zIndex={2}
                            label=''
                            viewStyle={{ minWidth: 225 }}
                            setFirstValue
                        />}
                        <Filter
                            filters={filters}
                            setFilters={setFilters}
                            groupId={groupId}
                            setGroupId={setGroupId}
                            onStatusChange={(isOpen) => setIsFilterOpen(isOpen)}
                        />
                        {dashboardData && <ExportCsvButton
                            dashboardData={dashboardData}
                            filters={filters}
                        />}
                    </View>
                    : <CustomButton
                        text={getRegisterButtonText()}
                        onPress={() => setIsModalVisible(true)}
                    />
                }
            </>}
        />
        <Card style={{ zIndex: -99 }}>
            {tabs.length > 0 &&
                <Tabs
                    tabs={tabs}
                    selectedTab={route.params ? route.params.selectedTab : undefined}
                    endContent={
                        (currentTab === 'dashboard' && dashboardCalculationTime != 0)
                        && <View style={{ alignContent: 'center', alignSelf: 'flex-end' }}>
                            <Badge mode='SQUARE' size='medium' color='gray'>{dashboardCalculationTime ? `${translate('lastUpdate')}: ${moment(dashboardCalculationTime).format('DD/MM/YYYY - HH:mm:00')}` : translate('thereIsNoUpdate')}</Badge>
                        </View>
                    }
                >
                    {({ selectedTab }) => {
                        setCurrentTab(selectedTab as PlateMonitoringDashboardSelectedTab);
                        if (selectedTab === 'dashboard') {
                            if (groupId === 0) {
                                return <View style={styles.allSpaceCentered}>
                                    <MyAppText style={styles.mainHeaderText}>{translate('requiredPlateGroupSelection')}</MyAppText>
                                </View>;
                            }
                            return <Dashboard
                                filters={filters}
                                setFilters={setFilters}
                                navigation={navigation}
                                setLastDashboardCalculation={setDashboardCalculationTime}
                                dashboardData={dashboardData}
                                setDashboardData={setDashboardData}
                            />;
                        } else if (selectedTab === 'plateGroupsManagement') {
                            return <PlateGroupTable
                                showCreateModal={isModalVisible}
                                setShowCreateModal={setIsModalVisible}
                            />;
                        } else if (selectedTab === 'registeredPlates') {
                            return <RegisteredPlateTable
                                showCreateModal={isModalVisible}
                                setShowCreateModal={setIsModalVisible}
                            />;
                        }
                    }}
                </Tabs>
            }
        </Card>
    </Container>;

}

const styleSheet = createStyleSheet((theme) => ({
    mainHeaderText: {
        fontSize: 22,
        color: theme.colors.neutral['500'],
        fontWeight: '700',
    },
    allSpaceCentered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
